import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react"
import {
  League,
  MatchResult,
  TeamHubLeagueResponse,
  UpcomingFixture,
} from "../models/league"
import { LineupPosition, TeamSingle } from "../models/team"
import LeagueService from "../services/league"
import { Team } from "../models/team"
import { useUser } from "./useUser"
import useNotyf from "./useNotify"
import { User } from "../models/user"
import { LeagueStats } from "../models/leagueStats"

interface LeagueContextType {
  league: League | null
  getLeagueById: (leagueId: string) => void
  leagueTeams: Team[] | null
  userTeam: TeamSingle | null
  lineup: LineupPosition[]
  userRoster: User[]
  setLineup: Dispatch<SetStateAction<LineupPosition[]>>
  manager: User | null
  leagueStats: LeagueStats | null
  matchResults: MatchResult[]
  upcomingFixtures: UpcomingFixture[]
  setTeamHubLeague: (response: TeamHubLeagueResponse) => void
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined)

export function LeagueProvider({ children }: LeagueProviderProps) {
  const [league, setLeague] = useState<League | null>(null)
  const [leagueTeams, setLeagueTeams] = useState<Team[]>([])
  const [userTeam, setUserTeam] = useState<TeamSingle | null>(null)
  const [userRoster, setUserRoster] = useState<User[]>([])
  const [lineup, setLineup] = useState<LineupPosition[]>([])
  const [manager, setManager] = useState<User | null>(null)
  const [leagueStats, setLeagueStats] = useState<LeagueStats | null>(null)
  const [matchResults, setMatchResults] = useState<MatchResult[]>([])
  const [upcomingFixtures, setUpcomingFixtures] = useState<UpcomingFixture[]>([])

  const { user } = useUser()
  const notyf = useNotyf()

  const getLeagueById = useCallback(async (leagueId: string) => {
    try {
      const response = await LeagueService.getTeamHubLeague(leagueId);
      setTeamHubLeague(response);
    } catch (err: any) {
      console.error("[useLeague] League fetching failed: ", err);
      notyf.error("There was a problem fetching your league. Please refresh and try again.");
    }
  }, [LeagueService, notyf]);
    
  const setTeamHubLeague = (response: TeamHubLeagueResponse) => {
    setLeague(response.league);
    setLeagueTeams(response.league.teams);
    setUserTeam(response.userTeam);
    setUserRoster(response.userTeam?.roster || []);
    setLineup(response.userTeam?.lineup || []);
    setManager(response.userTeam?.managerId);
    setLeagueStats(response.leagueStats);
    setMatchResults(response.matchResults);
    setUpcomingFixtures(response.upcomingFixtures);
  };

  // Initiating league
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      (async () => {
        if (user?.leagues?.length) {
          await getLeagueById(user.leagues[0]);
        }
      })();
      firstUpdate.current = false;
    }
  }, [JSON.stringify(user?.leagues), getLeagueById]);

  return (
    <LeagueContext.Provider
      value={{
        league,
        getLeagueById,
        leagueTeams,
        userTeam,
        lineup,
        setLineup,
        userRoster,
        manager,
        leagueStats,
        matchResults,
        upcomingFixtures,
        setTeamHubLeague,
      }}
    >
      {children}
    </LeagueContext.Provider>
  )
}

export function useLeague() {
  const context = useContext(LeagueContext)
  if (context === undefined) {
    throw new Error("useLeagues must be used within a LeagueProvider")
  }
  return context
}

interface LeagueProviderProps {
  children: ReactNode
}
